import React, { useEffect, useMemo, useState } from 'react';
import { db, functions } from '../firebase';
import { collection, collectionGroup, getDoc, getDocs, doc, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  ArrowLeft, ArrowRight, BookOpen, Loader2, CheckCircle2, ShoppingCart,
  Plus, Minus, Trash2,
} from 'lucide-react';
import { bucketProductType, isPhysicalProduct } from '../lib/productCategory';
import { computeBlockShipping } from '../lib/shopShipping';

const isPhysical = isPhysicalProduct;

// A product is purchasable in the guest shop only if it goes through Stripe.
// Exclude Amazon/external-link products and anything explicitly portal-only.
function isGuestPurchasable(product) {
  if (product.available !== true) return false;
  if (product.portalOnly === true) return false;
  const type = (product.purchaseType || '').toString().toLowerCase();
  if (type === 'amazon' || type === 'external') return false;
  return true;
}

export default function ShopPage({ authorSlug = null, bookSlug = null, format = null }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [books, setBooks] = useState([]); // each: { id, authorId, authorName, ... }
  const [authorsMap, setAuthorsMap] = useState({}); // authorId -> displayName
  const [authorSlugs, setAuthorSlugs] = useState({}); // authorId -> shopSlug

  // Format-card highlighting (?format=audiobook deep link) and blurb clamping.
  const [highlightProductId, setHighlightProductId] = useState(null);
  const [blurbExpanded, setBlurbExpanded] = useState(false);
  const [blurbOverflows, setBlurbOverflows] = useState(false);
  const productRefs = React.useRef({});
  const blurbRef = React.useRef(null);
  const autoNavDone = React.useRef(false);
  const formatApplied = React.useRef(false);

  const [selectedBook, setSelectedBook] = useState(null);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  // Multi-item cart, keyed by productId -> quantity. Scoped to the selected
  // book (reset whenever a different book is chosen).
  const [cart, setCart] = useState({});

  const [details, setDetails] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    addressLine1: '', addressLine2: '', city: '', postcode: '', country: 'United Kingdom',
    joinMailingList: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Email verification state
  const [codeSent, setCodeSent] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [codeError, setCodeError] = useState('');
  const [resendAvailableAt, setResendAvailableAt] = useState(0);
  const [now, setNow] = useState(Date.now());

  // Promo code state
  const [promoInput, setPromoInput] = useState('');
  const [promoApplying, setPromoApplying] = useState(false);
  const [promoApplied, setPromoApplied] = useState(null); // { id, code, discountType, discountValue }
  const [promoError, setPromoError] = useState('');

  // Tick for resend timer
  useEffect(() => {
    if (!codeSent) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [codeSent]);

  // Load all books and author display names
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collectionGroup(db, 'books'));
        const list = [];
        const authorIds = new Set();
        snap.forEach(d => {
          const data = d.data();
          const status = data.status;
          if (status !== 'launched' && status !== 'pre_launch_active') return;
          const parentAuthorId = d.ref.parent.parent?.id;
          if (!parentAuthorId) return;
          authorIds.add(parentAuthorId);
          // AuthorStore reads book.coverUrl — match that field.
          const cover = data.coverUrl || data.coverImageUrl || '';
          list.push({
            id: d.id,
            authorId: parentAuthorId,
            title: data.title || 'Untitled',
            series: data.series || '',
            seriesOrder: data.seriesOrder || data.bookNumber || 0,
            coverImageUrl: cover,
            blurb: data.blurb || data.description || '',
            slug: data.slug || '',
            status,
          });
        });

        const authorMap = {};
        const slugMap = {};
        await Promise.all([...authorIds].map(async aid => {
          let name = 'Author';
          try {
            const aSnap = await getDoc(doc(db, 'authors', aid));
            if (aSnap.exists()) {
              const ad = aSnap.data();
              name = ad.penName || ad.name || ad.displayName || name;
              if (ad.shopSlug) slugMap[aid] = ad.shopSlug;
            }
            const brandSnap = await getDoc(doc(db, 'authors', aid, 'settings', 'branding'));
            if (brandSnap.exists()) {
              const bd = brandSnap.data();
              if (bd.displayName) name = bd.displayName;
            }
          } catch { /* skip */ }
          authorMap[aid] = name;
        }));
        setAuthorsMap(authorMap);
        setAuthorSlugs(slugMap);
        setBooks(list);
      } catch (err) {
        console.error('Failed to load books:', err);
        setError('Could not load books. Please try again.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Group books: author -> series -> books
  const grouped = useMemo(() => {
    const byAuthor = {};
    books.forEach(b => {
      const authorName = authorsMap[b.authorId] || 'Author';
      if (!byAuthor[authorName]) byAuthor[authorName] = {};
      const seriesKey = b.series || '—';
      if (!byAuthor[authorName][seriesKey]) byAuthor[authorName][seriesKey] = [];
      byAuthor[authorName][seriesKey].push(b);
    });
    Object.values(byAuthor).forEach(seriesMap => {
      Object.values(seriesMap).forEach(arr =>
        arr.sort((a, b) => (a.seriesOrder || 0) - (b.seriesOrder || 0) || a.title.localeCompare(b.title))
      );
    });
    return byAuthor;
  }, [books, authorsMap]);

  // ── Author scoping (shareable /reader/shop/:authorSlug links) ──
  // Resolve the requested authorSlug to an authorId. An unknown slug resolves to
  // null, so the shop silently falls back to the full listing.
  const scopedAuthorId = useMemo(() => {
    if (!authorSlug) return null;
    const want = authorSlug.trim().toLowerCase();
    const hit = Object.entries(authorSlugs).find(([, s]) => (s || '').toLowerCase() === want);
    return hit ? hit[0] : null;
  }, [authorSlug, authorSlugs]);

  // Flat, sorted list of the scoped author's books (null when not scoped).
  const scopedBooks = useMemo(() => {
    if (!scopedAuthorId) return null;
    return books
      .filter(b => b.authorId === scopedAuthorId)
      .sort((a, b) => (a.seriesOrder || 0) - (b.seriesOrder || 0) || a.title.localeCompare(b.title));
  }, [scopedAuthorId, books]);

  // Deep link straight to a book's product page (/reader/shop/:authorSlug/:bookSlug).
  // Runs once after books have loaded. An unknown bookSlug just leaves the reader
  // on the scoped browse step.
  useEffect(() => {
    if (loading || autoNavDone.current) return;
    if (scopedAuthorId && bookSlug && scopedBooks) {
      autoNavDone.current = true;
      const want = bookSlug.trim().toLowerCase();
      const book = scopedBooks.find(b => (b.slug || '').toLowerCase() === want);
      if (book) selectBook(book);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, scopedAuthorId, bookSlug, scopedBooks]);

  // Highlight + scroll to the format card named by ?format= once products load.
  useEffect(() => {
    if (step !== 2 || !format || products.length === 0 || formatApplied.current) return;
    const want = format.trim().toLowerCase();
    const match =
      products.find(p => bucketProductType(p) === want) ||
      products.find(p => `${p.name || ''} ${p.productType || ''}`.toLowerCase().includes(want));
    if (!match) return;
    formatApplied.current = true;
    setHighlightProductId(match.id);
    requestAnimationFrame(() => {
      const el = productRefs.current[match.id];
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    const t = setTimeout(() => setHighlightProductId(null), 4000);
    return () => clearTimeout(t);
  }, [step, format, products]);

  // Decide whether the blurb overflows 3 lines (so we only show the toggle when
  // it actually clamps). Re-measures on book change and on resize.
  useEffect(() => {
    if (step !== 2 || blurbExpanded) return;
    const measure = () => {
      const el = blurbRef.current;
      if (el) setBlurbOverflows(el.scrollHeight > el.clientHeight + 1);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [step, selectedBook, blurbExpanded]);

  async function selectBook(book) {
    setSelectedBook(book);
    setCart({});
    setBlurbExpanded(false);
    setBlurbOverflows(false);
    formatApplied.current = false;
    setHighlightProductId(null);
    setProductsLoading(true);
    setStep(2);
    try {
      const prodQ = query(
        collection(db, 'authors', book.authorId, 'books', book.id, 'products'),
        where('available', '==', true)
      );
      const snap = await getDocs(prodQ);
      const list = [];
      snap.forEach(d => {
        const data = { id: d.id, ...d.data() };
        // Debug: inspect every product doc so we can see which fields we need to filter on
        console.log('[ShopPage] product doc', d.id, data, 'purchasable?', isGuestPurchasable(data));
        if (isGuestPurchasable(data)) list.push(data);
      });
      list.sort((a, b) => (a.price || 0) - (b.price || 0));
      setProducts(list);
    } catch (err) {
      console.error('Failed to load products:', err);
      setError('Could not load purchase options.');
    } finally {
      setProductsLoading(false);
    }
  }

  // ── Cart helpers ──
  const cartItems = useMemo(() => (
    Object.entries(cart)
      .map(([pid, qty]) => {
        const product = products.find(p => p.id === pid);
        return product ? { product, quantity: qty } : null;
      })
      .filter(Boolean)
  ), [cart, products]);

  const cartCount = cartItems.reduce((n, ci) => n + ci.quantity, 0);
  const cartHasItems = cartItems.length > 0;

  function addToCart(product) {
    setCart(c => ({ ...c, [product.id]: Math.min(10, (c[product.id] || 0) + 1) }));
  }
  function setQty(productId, qty) {
    const n = Math.max(0, Math.min(10, qty));
    setCart(c => {
      const next = { ...c };
      if (n <= 0) delete next[productId];
      else next[productId] = n;
      return next;
    });
    // Removing the last physical item can invalidate an applied promo's free
    // shipping context, but the server revalidates, so leave promo as-is.
  }
  function removeFromCart(productId) { setQty(productId, 0); }

  function goToDetails() {
    if (!cartHasItems) return;
    setStep(3);
  }

  const needsAddress = cartItems.some(ci => isPhysical(ci.product));

  function emailValid(e) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || '').trim());
  }

  function detailsValid() {
    if (!cartHasItems) return false;
    if (!details.firstName.trim() || !details.lastName.trim()) return false;
    if (!emailValid(details.email)) return false;
    if (!emailVerified) return false;
    if (needsAddress) {
      if (!details.addressLine1.trim() || !details.city.trim() || !details.postcode.trim() || !details.country.trim()) return false;
    }
    if (promoInput.trim() && !promoApplied) return false;
    return true;
  }

  function updateEmail(value) {
    setDetails(d => ({ ...d, email: value }));
    // Any change resets verification.
    setEmailVerified(false);
    setCodeSent(false);
    setCodeInput('');
    setCodeError('');
  }

  async function sendVerificationCode() {
    if (!emailValid(details.email)) {
      setCodeError('Enter a valid email address first.');
      return;
    }
    setSendingCode(true);
    setCodeError('');
    try {
      const fn = httpsCallable(functions, 'sendGuestVerificationCode');
      await fn({ email: details.email.trim().toLowerCase() });
      setCodeSent(true);
      setResendAvailableAt(Date.now() + 30 * 1000);
    } catch (err) {
      console.error(err);
      setCodeError(err.message || 'Could not send code. Please try again.');
    } finally {
      setSendingCode(false);
    }
  }

  async function verifyCode() {
    if (codeInput.trim().length !== 6) {
      setCodeError('Enter the 6-digit code.');
      return;
    }
    setVerifyingCode(true);
    setCodeError('');
    try {
      const fn = httpsCallable(functions, 'verifyGuestCode');
      const res = await fn({ email: details.email.trim().toLowerCase(), code: codeInput.trim() });
      if (res.data?.success) {
        setEmailVerified(true);
      } else {
        const r = res.data?.reason;
        setCodeError(
          r === 'expired' ? 'Code expired — please request a new one.' :
          r === 'used' ? 'That code has already been used.' :
          'Incorrect code.'
        );
      }
    } catch (err) {
      console.error(err);
      setCodeError(err.message || 'Verification failed.');
    } finally {
      setVerifyingCode(false);
    }
  }

  async function applyPromo() {
    const code = promoInput.trim();
    if (!code || !selectedBook || !cartHasItems) return;
    setPromoApplying(true);
    setPromoError('');
    setPromoApplied(null);
    try {
      // Validate against a representative cart item — prefer the first physical
      // item so free-shipping / physical-only codes resolve correctly.
      const repItem = cartItems.find(ci => isPhysical(ci.product)) || cartItems[0];
      const fn = httpsCallable(functions, 'validatePromoCode');
      const res = await fn({
        authorId: selectedBook.authorId,
        code,
        productType: bucketProductType(repItem.product),
        productId: repItem.product.id,
      });
      if (res.data?.valid) {
        setPromoApplied({
          id: res.data.promoCodeId,
          code: res.data.code,
          discountType: res.data.discountType,
          discountValue: res.data.discountValue,
          freeShipping: !!res.data.freeShipping,
        });
      } else {
        setPromoError(res.data?.reason || 'Code not recognised');
      }
    } catch (err) {
      setPromoError(err?.message || 'Could not validate code. Please try again.');
    } finally {
      setPromoApplying(false);
    }
  }

  // Per-unit discounted price for one product (discount never touches shipping).
  function unitDiscountedPrice(product) {
    const base = Number(product?.price || 0);
    if (!promoApplied) return base;
    if (promoApplied.discountType === 'percentage') {
      return Math.max(0, base * (1 - Number(promoApplied.discountValue || 0) / 100));
    }
    if (promoApplied.discountType === 'fixed') {
      return Math.max(0, base - Number(promoApplied.discountValue || 0));
    }
    return base;
  }

  // Cart totals. Discount applies to the product subtotal only; shipping is a
  // single block-of-five charge mirroring the authoritative server calc.
  const cartTotals = useMemo(() => {
    let originalSubtotal = 0;
    let subtotal = 0;
    for (const { product, quantity } of cartItems) {
      originalSubtotal += Number(product.price || 0) * quantity;
      subtotal += unitDiscountedPrice(product) * quantity;
    }
    const breakdown = computeBlockShipping(cartItems, details.country);
    const shipping = promoApplied?.freeShipping ? 0 : breakdown.shippingCost;
    return {
      originalSubtotal,
      subtotal,
      discount: originalSubtotal - subtotal,
      shipping,
      total: subtotal + shipping,
      ...breakdown,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartItems, promoApplied, details.country]);

  async function completePurchase() {
    if (!selectedBook || !cartHasItems || !detailsValid()) return;
    setSubmitting(true);
    setError('');
    try {
      const authorId = selectedBook.authorId;
      const authorName = authorsMap[authorId] || '';
      const fullName = `${details.firstName.trim()} ${details.lastName.trim()}`.trim();
      const emailLower = details.email.trim().toLowerCase();

      // Mailing list opt-in is handled server-side by the Stripe webhook after
      // successful payment — this avoids adding emails if the user abandons checkout.

      const shippingAddress = needsAddress
        ? {
            line1: details.addressLine1.trim(),
            line2: details.addressLine2.trim(),
            city: details.city.trim(),
            postalCode: details.postcode.trim(),
            country: details.country.trim(),
          }
        : {};

      const items = cartItems.map(({ product, quantity }) => ({
        productId: product.id,
        bookId: selectedBook.id,
        quantity,
      }));

      const createCheckout = httpsCallable(functions, 'createOrderCheckout');
      const result = await createCheckout({
        authorId,
        items,
        bookId: selectedBook.id,
        readerName: fullName,
        guestEmail: emailLower,
        guestPhone: details.phone.trim(),
        shippingAddress,
        mailingListOptIn: !!details.joinMailingList,
        authorName,
        ...(promoApplied ? { promoCodeId: promoApplied.id, promoCode: promoApplied.code, freeShipping: !!promoApplied.freeShipping } : {}),
      });

      if (result.data?.sessionUrl) {
        setStep(5);
        window.location.href = result.data.sessionUrl;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Checkout failed. Please try again.');
      setSubmitting(false);
    }
  }

  // --- RENDER ---
  return (
    <div className="min-h-screen bg-slate-900 text-slate-100">
      <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-slate-600 to-teal-600">
              <BookOpen className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Narratoria Shop</h1>
              <p className="text-xs text-slate-400">Buy books — no account required</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { window.location.href = '/reader'; }}
            className="gap-1.5 border-slate-600 bg-slate-800 text-slate-200 hover:bg-slate-700"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Narratoria
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        {/* Stepper */}
        <div className="mb-8 flex items-center gap-2 text-xs text-slate-400">
          {['Browse', 'Choose', 'Details', 'Payment', 'Done'].map((label, i) => {
            const n = i + 1;
            const active = step === n;
            const done = step > n;
            return (
              <React.Fragment key={label}>
                <div className={`flex items-center gap-1.5 ${active ? 'text-teal-400' : done ? 'text-slate-300' : 'text-slate-500'}`}>
                  <div className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold ${active ? 'bg-teal-500 text-white' : done ? 'bg-slate-600 text-white' : 'bg-slate-800 text-slate-400'}`}>
                    {done ? <CheckCircle2 className="h-3 w-3" /> : n}
                  </div>
                  <span className="hidden sm:inline">{label}</span>
                </div>
                {n < 5 && <div className="h-px flex-1 bg-slate-800" />}
              </React.Fragment>
            );
          })}
        </div>

        {error && (
          <div className="mb-6 rounded-lg border border-red-800 bg-red-950/40 px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        )}

        {/* STEP 1 — Browse */}
        {step === 1 && (
          <section>
            <h2 className="mb-6 text-2xl font-bold text-white">
              {scopedAuthorId ? `Books by ${authorsMap[scopedAuthorId] || 'this author'}` : 'Available Books'}
            </h2>
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-teal-400" />
              </div>
            ) : (scopedBooks ? scopedBooks.length === 0 : Object.keys(grouped).length === 0) ? (
              <Card className="border-slate-700 bg-slate-800/60 p-12 text-center">
                <BookOpen className="mx-auto mb-3 h-10 w-10 text-slate-500" />
                <p className="text-slate-400">No books are currently available.</p>
              </Card>
            ) : scopedBooks ? (
              /* Author-scoped view — flat grid, no author/series headings */
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {scopedBooks.map(book => (
                  <button key={book.id} onClick={() => selectBook(book)} className="text-left">
                    <Card className="flex h-full gap-3 overflow-hidden border-slate-700 bg-slate-800/60 p-3 transition-all hover:border-teal-500 hover:bg-slate-800">
                      <div className="h-28 w-20 flex-shrink-0 overflow-hidden rounded bg-slate-700">
                        {book.coverImageUrl ? (
                          <img src={book.coverImageUrl} alt={book.title} className="h-full w-full object-cover" />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center">
                            <BookOpen className="h-6 w-6 text-slate-500" />
                          </div>
                        )}
                      </div>
                      <div className="flex flex-1 flex-col">
                        <div className="text-sm font-semibold text-white">{book.title}</div>
                        {book.series && (
                          <div className="text-xs text-slate-400">
                            {book.series}{book.seriesOrder ? ` · Book ${book.seriesOrder}` : ''}
                          </div>
                        )}
                        <div className="mt-auto pt-2 text-xs font-medium text-teal-400">View options →</div>
                      </div>
                    </Card>
                  </button>
                ))}
              </div>
            ) : (
              <div className="space-y-8">
                {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([authorName, seriesMap]) => (
                  <div key={authorName}>
                    <h3 className="mb-3 text-xl font-semibold text-teal-300">{authorName}</h3>
                    {Object.entries(seriesMap).sort(([a], [b]) => a.localeCompare(b)).map(([seriesName, bookList]) => (
                      <div key={seriesName} className="mb-4 ml-4">
                        <h4 className="mb-2 text-sm font-medium uppercase tracking-wide text-slate-400">
                          {seriesName !== '—' ? seriesName : 'Standalone'}
                        </h4>
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                          {bookList.map(book => (
                            <button key={book.id} onClick={() => selectBook(book)} className="text-left">
                              <Card className="flex h-full gap-3 overflow-hidden border-slate-700 bg-slate-800/60 p-3 transition-all hover:border-teal-500 hover:bg-slate-800">
                                <div className="h-28 w-20 flex-shrink-0 overflow-hidden rounded bg-slate-700">
                                  {book.coverImageUrl ? (
                                    <img src={book.coverImageUrl} alt={book.title} className="h-full w-full object-cover" />
                                  ) : (
                                    <div className="flex h-full w-full items-center justify-center">
                                      <BookOpen className="h-6 w-6 text-slate-500" />
                                    </div>
                                  )}
                                </div>
                                <div className="flex flex-1 flex-col">
                                  <div className="text-sm font-semibold text-white">{book.title}</div>
                                  {book.series && (
                                    <div className="text-xs text-slate-400">
                                      {book.series}{book.seriesOrder ? ` · Book ${book.seriesOrder}` : ''}
                                    </div>
                                  )}
                                  <div className="mt-1 text-xs text-slate-500">{authorName}</div>
                                  <div className="mt-auto pt-2 text-xs font-medium text-teal-400">View options →</div>
                                </div>
                              </Card>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* STEP 2 — Choose product */}
        {step === 2 && selectedBook && (
          <section>
            <Button
              variant="ghost" size="sm"
              onClick={() => { setStep(1); setSelectedBook(null); setProducts([]); setCart({}); }}
              className="mb-4 gap-1.5 text-slate-300 hover:bg-slate-800 hover:text-teal-300"
            >
              <ArrowLeft className="h-4 w-4" /> Back to books
            </Button>
            <div className="grid gap-6 md:grid-cols-[220px_1fr]">
              <div>
                {/* Cover stays compact on mobile so the first format card is
                    reachable without scrolling on a deep-linked product page. */}
                <div className="mx-auto aspect-[2/3] w-28 overflow-hidden rounded-lg border border-slate-700 bg-slate-800 sm:w-36 md:mx-0 md:w-full">
                  {selectedBook.coverImageUrl ? (
                    <img src={selectedBook.coverImageUrl} alt={selectedBook.title} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full items-center justify-center"><BookOpen className="h-10 w-10 text-slate-500" /></div>
                  )}
                </div>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">{selectedBook.title}</h2>
                {selectedBook.series && (
                  <div className="mt-1 text-sm text-slate-400">
                    {selectedBook.series}{selectedBook.seriesOrder ? ` · Book ${selectedBook.seriesOrder}` : ''} · by {authorsMap[selectedBook.authorId]}
                  </div>
                )}
                {selectedBook.blurb && (
                  <div className="mt-4">
                    <p
                      ref={blurbRef}
                      className={`text-sm leading-relaxed text-slate-300 ${blurbExpanded ? 'whitespace-pre-wrap' : ''}`}
                      style={blurbExpanded ? undefined : {
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {selectedBook.blurb}
                    </p>
                    {blurbOverflows && (
                      <button
                        type="button"
                        onClick={() => setBlurbExpanded(v => !v)}
                        className="mt-1 text-xs font-semibold text-teal-400 hover:text-teal-300"
                        aria-expanded={blurbExpanded}
                      >
                        {blurbExpanded ? 'Read less ▴' : 'Read more ▾'}
                      </button>
                    )}
                  </div>
                )}

                <div className="mt-6">
                  <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-400">Choose your formats</h3>
                  {productsLoading ? (
                    <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-teal-400" /></div>
                  ) : products.length === 0 ? (
                    <Card className="border-slate-700 bg-slate-800/60 p-6 text-center text-sm text-slate-400">
                      No purchase options available yet.
                    </Card>
                  ) : (
                    <div className="space-y-2">
                      {products.map(p => {
                        const soldOut = p.stockQuantity != null && p.stockQuantity !== '' && Number(p.stockQuantity) <= 0;
                        const qty = cart[p.id] || 0;
                        const inCart = qty > 0;
                        const highlighted = highlightProductId === p.id;
                        return (
                          <div
                            key={p.id}
                            ref={el => { productRefs.current[p.id] = el; }}
                            className={`flex items-start gap-3 rounded-lg border p-4 transition-all ${
                              highlighted
                                ? 'border-teal-400 bg-slate-800 ring-2 ring-teal-400 ring-offset-2 ring-offset-slate-900'
                                : inCart
                                ? 'border-teal-500 bg-slate-800 ring-1 ring-teal-500/40'
                                : 'border-slate-700 bg-slate-800/40'
                            } ${soldOut ? 'opacity-50' : ''}`}
                          >
                            <div className="flex-1">
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <div className="font-semibold text-white">{p.name || p.productType}</div>
                                  {p.productType && p.name !== p.productType && (
                                    <div className="text-xs text-slate-400">{p.productType}</div>
                                  )}
                                  {p.description && <div className="mt-1 text-xs text-slate-400">{p.description}</div>}
                                </div>
                                <div className="text-right">
                                  <div className="text-lg font-bold text-teal-300">£{Number(p.price || 0).toFixed(2)}</div>
                                  {soldOut && <div className="text-xs text-red-400">Sold out</div>}
                                </div>
                              </div>
                              <div className="mt-3">
                                {soldOut ? null : inCart ? (
                                  <div className="flex items-center gap-2">
                                    <div className="flex items-center rounded-md border border-slate-600 bg-slate-900">
                                      <button
                                        type="button"
                                        onClick={() => setQty(p.id, qty - 1)}
                                        className="flex h-8 w-8 items-center justify-center text-slate-300 hover:text-teal-300"
                                        aria-label={`Decrease ${p.name || p.productType} quantity`}
                                      >
                                        <Minus className="h-4 w-4" />
                                      </button>
                                      <span className="w-8 text-center text-sm font-semibold text-white">{qty}</span>
                                      <button
                                        type="button"
                                        onClick={() => setQty(p.id, qty + 1)}
                                        disabled={qty >= 10}
                                        className="flex h-8 w-8 items-center justify-center text-slate-300 hover:text-teal-300 disabled:opacity-40"
                                        aria-label={`Increase ${p.name || p.productType} quantity`}
                                      >
                                        <Plus className="h-4 w-4" />
                                      </button>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => removeFromCart(p.id)}
                                      className="flex items-center gap-1 text-xs text-slate-400 hover:text-red-400"
                                    >
                                      <Trash2 className="h-3.5 w-3.5" /> Remove
                                    </button>
                                  </div>
                                ) : (
                                  <Button
                                    type="button"
                                    size="sm"
                                    onClick={() => addToCart(p)}
                                    className="gap-1.5 bg-teal-600 text-white hover:bg-teal-700"
                                  >
                                    <Plus className="h-4 w-4" /> Add to cart
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Cart summary */}
                {cartHasItems && (
                  <Card className="mt-6 border-slate-700 bg-slate-800/60 p-4">
                    <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-200">
                      <ShoppingCart className="h-4 w-4 text-teal-400" /> Your cart ({cartCount} {cartCount === 1 ? 'item' : 'items'})
                    </div>
                    <div className="space-y-1 text-sm">
                      {cartItems.map(({ product, quantity }) => (
                        <div key={product.id} className="flex justify-between text-slate-300">
                          <span>{product.name || product.productType} × {quantity}</span>
                          <span>£{(Number(product.price || 0) * quantity).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 border-t border-slate-700 pt-2 text-sm">
                      <div className="flex justify-between text-slate-400">
                        <span>Subtotal</span>
                        <span>£{cartTotals.subtotal.toFixed(2)}</span>
                      </div>
                      {cartTotals.physicalUnits > 0 && (
                        <div className="flex justify-between text-slate-400">
                          <span>Shipping (est.)</span>
                          <span>£{cartTotals.shipping.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="mt-1 flex justify-between font-semibold text-white">
                        <span>Total</span>
                        <span>£{cartTotals.total.toFixed(2)}</span>
                      </div>
                      {cartTotals.physicalUnits > 0 && (
                        <p className="mt-1 text-xs text-slate-500">
                          Shipping is one charge per five physical books. Final shipping is confirmed once you enter your address.
                        </p>
                      )}
                    </div>
                  </Card>
                )}

                <Button
                  onClick={goToDetails}
                  disabled={!cartHasItems}
                  className="mt-6 gap-1.5 bg-teal-600 text-white hover:bg-teal-700 disabled:bg-slate-700 disabled:text-slate-400"
                >
                  Continue to Purchase <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </section>
        )}

        {/* STEP 3 — Details */}
        {step === 3 && selectedBook && cartHasItems && (
          <section className="max-w-2xl">
            <Button
              variant="ghost" size="sm" onClick={() => setStep(2)}
              className="mb-4 gap-1.5 text-slate-300 hover:bg-slate-800 hover:text-teal-300"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </Button>
            <h2 className="mb-1 text-2xl font-bold text-white">Your Details</h2>
            <p className="mb-6 text-sm text-slate-400">
              No account needed — we just need these details to process your order.
            </p>

            <Card className="border-slate-700 bg-slate-800/60 p-6">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="firstName" className="text-slate-300">First Name *</Label>
                  <Input
                    id="firstName"
                    value={details.firstName}
                    onChange={e => setDetails({ ...details, firstName: e.target.value })}
                    className="mt-1 border-slate-600 bg-slate-900 text-white"
                  />
                </div>
                <div>
                  <Label htmlFor="lastName" className="text-slate-300">Last Name *</Label>
                  <Input
                    id="lastName"
                    value={details.lastName}
                    onChange={e => setDetails({ ...details, lastName: e.target.value })}
                    className="mt-1 border-slate-600 bg-slate-900 text-white"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="email" className="text-slate-300">Email Address *</Label>
                  <div className="mt-1 flex gap-2">
                    <Input
                      id="email" type="email"
                      value={details.email}
                      onChange={e => updateEmail(e.target.value)}
                      disabled={emailVerified}
                      className="flex-1 border-slate-600 bg-slate-900 text-white disabled:opacity-60"
                    />
                    {emailVerified ? (
                      <div className="flex items-center gap-1.5 rounded-md border border-teal-600 bg-teal-900/30 px-3 text-sm text-teal-300">
                        <CheckCircle2 className="h-4 w-4" /> Verified
                      </div>
                    ) : (
                      <Button
                        type="button"
                        onClick={sendVerificationCode}
                        disabled={!emailValid(details.email) || sendingCode}
                        className="shrink-0 bg-teal-600 text-white hover:bg-teal-700 disabled:bg-slate-700 disabled:text-slate-400"
                      >
                        {sendingCode ? <Loader2 className="h-4 w-4 animate-spin" /> : codeSent ? 'Resend' : 'Verify Email'}
                      </Button>
                    )}
                  </div>

                  {codeSent && !emailVerified && (
                    <div className="mt-3 rounded-lg border border-slate-700 bg-slate-900/60 p-4">
                      <Label htmlFor="verifyCode" className="text-slate-300">Enter the 6-digit code we emailed you</Label>
                      <div className="mt-1 flex gap-2">
                        <Input
                          id="verifyCode"
                          value={codeInput}
                          onChange={e => setCodeInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          maxLength={6}
                          inputMode="numeric"
                          placeholder="123456"
                          className="flex-1 border-slate-600 bg-slate-900 text-white tracking-widest"
                        />
                        <Button
                          type="button"
                          onClick={verifyCode}
                          disabled={codeInput.length !== 6 || verifyingCode}
                          className="shrink-0 bg-teal-600 text-white hover:bg-teal-700 disabled:bg-slate-700 disabled:text-slate-400"
                        >
                          {verifyingCode ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify'}
                        </Button>
                      </div>
                      {codeError && <p className="mt-2 text-xs text-red-400">{codeError}</p>}
                      <div className="mt-2 text-xs text-slate-400">
                        {now >= resendAvailableAt ? (
                          <button type="button" onClick={sendVerificationCode} className="text-teal-400 hover:underline">
                            Resend code
                          </button>
                        ) : (
                          <>Resend available in {Math.ceil((resendAvailableAt - now) / 1000)}s</>
                        )}
                      </div>
                    </div>
                  )}
                  {codeError && !codeSent && <p className="mt-1 text-xs text-red-400">{codeError}</p>}
                </div>

                <div className="sm:col-span-2">
                  <Label htmlFor="phone" className="text-slate-300">Phone Number (optional)</Label>
                  <Input
                    id="phone" type="tel"
                    value={details.phone}
                    onChange={e => setDetails({ ...details, phone: e.target.value })}
                    placeholder="+44 7700 000000"
                    className="mt-1 border-slate-600 bg-slate-900 text-white"
                  />
                </div>

                {needsAddress && (
                  <>
                    <div className="sm:col-span-2 mt-2">
                      <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-400">Shipping Address</h3>
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="addressLine1" className="text-slate-300">Address Line 1 *</Label>
                      <Input id="addressLine1" value={details.addressLine1}
                        onChange={e => setDetails({ ...details, addressLine1: e.target.value })}
                        className="mt-1 border-slate-600 bg-slate-900 text-white" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="addressLine2" className="text-slate-300">Address Line 2</Label>
                      <Input id="addressLine2" value={details.addressLine2}
                        onChange={e => setDetails({ ...details, addressLine2: e.target.value })}
                        className="mt-1 border-slate-600 bg-slate-900 text-white" />
                    </div>
                    <div>
                      <Label htmlFor="city" className="text-slate-300">City *</Label>
                      <Input id="city" value={details.city}
                        onChange={e => setDetails({ ...details, city: e.target.value })}
                        className="mt-1 border-slate-600 bg-slate-900 text-white" />
                    </div>
                    <div>
                      <Label htmlFor="postcode" className="text-slate-300">Postcode *</Label>
                      <Input id="postcode" value={details.postcode}
                        onChange={e => setDetails({ ...details, postcode: e.target.value })}
                        className="mt-1 border-slate-600 bg-slate-900 text-white" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="country" className="text-slate-300">Country *</Label>
                      <Input
                        id="country"
                        list="countries"
                        value={details.country}
                        onChange={e => setDetails({ ...details, country: e.target.value })}
                        placeholder="Start typing or pick from list"
                        className="mt-1 border-slate-600 bg-slate-900 text-white"
                      />
                      <datalist id="countries">
                        {[
                          'United Kingdom', 'Ireland', 'United States', 'Canada', 'Australia', 'New Zealand',
                          'Austria', 'Belgium', 'Bulgaria', 'Croatia', 'Cyprus', 'Czech Republic', 'Denmark',
                          'Estonia', 'Finland', 'France', 'Germany', 'Greece', 'Hungary', 'Iceland', 'Italy',
                          'Latvia', 'Liechtenstein', 'Lithuania', 'Luxembourg', 'Malta', 'Netherlands', 'Norway',
                          'Poland', 'Portugal', 'Romania', 'Slovakia', 'Slovenia', 'Spain', 'Sweden', 'Switzerland',
                          'Albania', 'Andorra', 'Belarus', 'Bosnia and Herzegovina', 'Kosovo', 'Moldova', 'Monaco',
                          'Montenegro', 'North Macedonia', 'San Marino', 'Serbia', 'Ukraine', 'Vatican City',
                          'South Africa', 'Nigeria', 'Kenya', 'Ghana', 'Zimbabwe', 'Tanzania', 'Uganda', 'Zambia',
                          'Botswana', 'Ethiopia', 'Morocco', 'Algeria', 'Tunisia', 'Namibia', 'Rwanda', 'Senegal',
                          'Ivory Coast', 'Cameroon', 'Mauritius',
                          'Brazil', 'Argentina', 'Chile', 'Colombia', 'Mexico', 'Peru', 'Uruguay', 'Venezuela',
                          'Ecuador', 'Bolivia', 'Paraguay', 'Costa Rica', 'Panama', 'Dominican Republic', 'Jamaica',
                          'Trinidad and Tobago', 'Cuba', 'Guatemala',
                          'India', 'Pakistan', 'Bangladesh', 'Sri Lanka', 'Nepal', 'Bhutan', 'Maldives', 'Afghanistan',
                          'Philippines', 'Malaysia', 'Singapore', 'Indonesia', 'Thailand', 'Vietnam', 'Cambodia',
                          'Laos', 'Myanmar', 'Brunei',
                          'Japan', 'South Korea', 'North Korea', 'China', 'Hong Kong', 'Taiwan', 'Mongolia', 'Macau',
                          'United Arab Emirates', 'Saudi Arabia', 'Israel', 'Turkey', 'Egypt', 'Jordan', 'Lebanon',
                          'Qatar', 'Kuwait', 'Bahrain', 'Oman', 'Iraq', 'Iran', 'Yemen', 'Syria', 'Palestine',
                          'Russia', 'Kazakhstan', 'Uzbekistan', 'Georgia', 'Armenia', 'Azerbaijan', 'Kyrgyzstan',
                          'Tajikistan', 'Turkmenistan',
                          'Fiji', 'Papua New Guinea', 'Samoa', 'Tonga', 'Solomon Islands',
                        ].map(c => <option key={c} value={c} />)}
                      </datalist>
                    </div>
                  </>
                )}

                {/* Promo code */}
                <div className="sm:col-span-2 mt-2 rounded-lg border border-slate-700 bg-slate-900/60 p-4">
                  <Label htmlFor="promo" className="text-slate-300">Promo code</Label>
                  <div className="mt-1 flex gap-2">
                    <Input
                      id="promo"
                      value={promoInput}
                      onChange={e => { setPromoInput(e.target.value.toUpperCase()); setPromoApplied(null); setPromoError(''); }}
                      disabled={!!promoApplied}
                      placeholder="ENTER CODE"
                      className="flex-1 border-slate-600 bg-slate-900 text-white disabled:opacity-60"
                    />
                    {promoApplied ? (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => { setPromoApplied(null); setPromoInput(''); setPromoError(''); }}
                        className="shrink-0 border-slate-600 bg-slate-800 text-slate-200 hover:bg-slate-700"
                      >
                        Remove
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        onClick={applyPromo}
                        disabled={!promoInput.trim() || promoApplying}
                        className="shrink-0 bg-teal-600 text-white hover:bg-teal-700 disabled:bg-slate-700 disabled:text-slate-400"
                      >
                        {promoApplying ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
                      </Button>
                    )}
                  </div>
                  {promoApplied && (
                    <p className="mt-2 flex items-center gap-1.5 text-xs text-teal-300">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      {promoApplied.freeShipping
                        ? '🎉 Free shipping applied!'
                        : `Code applied — you save £${cartTotals.discount.toFixed(2)}`}
                    </p>
                  )}
                  {promoError && <p className="mt-2 text-xs text-red-400">{promoError}</p>}
                </div>

                <div className="sm:col-span-2 mt-2 flex items-start gap-3 rounded-lg border border-slate-700 bg-slate-900/60 p-4">
                  <Checkbox
                    id="mailingList"
                    checked={details.joinMailingList}
                    onCheckedChange={v => setDetails({ ...details, joinMailingList: !!v })}
                    className="mt-0.5 border-slate-500 data-[state=checked]:bg-teal-600 data-[state=checked]:border-teal-600"
                  />
                  <label htmlFor="mailingList" className="cursor-pointer text-sm text-slate-300">
                    Yes, add me to <span className="font-medium text-teal-300">{authorsMap[selectedBook.authorId]}</span>'s mailing list for news and updates.
                  </label>
                </div>
              </div>

              {/* Order summary */}
              <div className="mt-6 border-t border-slate-700 pt-4">
                <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-400">{selectedBook.title}</div>
                <div className="space-y-1.5">
                  {cartItems.map(({ product, quantity }) => {
                    const unit = unitDiscountedPrice(product);
                    const discounted = promoApplied && !promoApplied.freeShipping && unit < Number(product.price || 0);
                    return (
                      <div key={product.id} className="flex items-start justify-between gap-3 text-sm">
                        <div className="flex flex-1 items-center gap-2">
                          <span className="text-slate-300">{product.name || product.productType}</span>
                          <span className="text-xs text-slate-500">× {quantity}</span>
                          <button
                            type="button"
                            onClick={() => removeFromCart(product.id)}
                            className="text-slate-500 hover:text-red-400"
                            aria-label={`Remove ${product.name || product.productType}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <div className="text-right text-sm">
                          {discounted ? (
                            <>
                              <span className="line-through text-slate-500">£{(Number(product.price || 0) * quantity).toFixed(2)}</span>{' '}
                              <span className="text-teal-300">£{(unit * quantity).toFixed(2)}</span>
                            </>
                          ) : (
                            <span className="text-slate-300">£{(Number(product.price || 0) * quantity).toFixed(2)}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-3 space-y-1 border-t border-slate-700 pt-3 text-sm">
                  <div className="flex justify-between text-slate-400">
                    <span>Subtotal</span>
                    <span>£{cartTotals.subtotal.toFixed(2)}</span>
                  </div>
                  {cartTotals.discount > 0 && (
                    <div className="flex justify-between text-teal-300">
                      <span>Discount</span>
                      <span>-£{cartTotals.discount.toFixed(2)}</span>
                    </div>
                  )}
                  {needsAddress && (
                    <div className="flex justify-between text-slate-400">
                      <span>
                        Shipping
                        {cartTotals.shippingUnits > 1 ? ` (${cartTotals.shippingUnits} × £${cartTotals.ratePerBlock.toFixed(2)})` : ''}
                      </span>
                      <span>
                        {promoApplied?.freeShipping
                          ? <span className="text-teal-300">FREE</span>
                          : `£${cartTotals.shipping.toFixed(2)}`}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between pt-1 text-base font-bold text-white">
                    <span>Total</span>
                    <span>£{cartTotals.total.toFixed(2)}</span>
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <Button
                    onClick={completePurchase}
                    disabled={!detailsValid() || submitting}
                    className="gap-1.5 bg-teal-600 text-white hover:bg-teal-700 disabled:bg-slate-700 disabled:text-slate-400"
                  >
                    {submitting ? (
                      <><Loader2 className="h-4 w-4 animate-spin" /> Processing…</>
                    ) : (
                      <><ShoppingCart className="h-4 w-4" /> Complete Purchase</>
                    )}
                  </Button>
                </div>
              </div>
            </Card>
          </section>
        )}

        {/* STEP 5 — Confirmation (reached if Stripe redirect fails to fire) */}
        {step === 5 && (
          <section className="max-w-xl">
            <Card className="border-slate-700 bg-slate-800/60 p-8 text-center">
              <CheckCircle2 className="mx-auto mb-3 h-12 w-12 text-teal-400" />
              <h2 className="text-xl font-bold text-white">Redirecting you to payment…</h2>
              <p className="mt-2 text-sm text-slate-400">
                If you are not redirected automatically,{' '}
                <button onClick={completePurchase} className="text-teal-400 underline">click here</button>.
              </p>
              <Button
                variant="outline" className="mt-6 border-slate-600 bg-slate-800 text-slate-200 hover:bg-slate-700"
                onClick={() => { window.location.href = '/reader'; }}
              >
                Back to Narratoria
              </Button>
            </Card>
          </section>
        )}
      </main>
    </div>
  );
}
